/**
 * The operations the backend contract defines — a typed mirror of the `paths` section of
 * admin-api.openapi.yaml (tests/contract.test.ts fails if the two drift apart). Path templates
 * use `{param}` placeholders filled from `CallOptions.params`.
 *
 * src/api/client.ts only accepts these operation ids, so no other endpoint can be called.
 */
export const OPERATIONS = {
  getLiveness: { method: "GET", path: "/health" },
  getReadiness: { method: "GET", path: "/ready" },

  // Admin auth — a separate Better Auth instance from dealer auth (/api/auth).
  signInEmail: { method: "POST", path: "/admin/auth/sign-in/email" },
  signOut: { method: "POST", path: "/admin/auth/sign-out" },
  getSession: { method: "GET", path: "/admin/auth/get-session" },
  getAdminIdentity: { method: "GET", path: "/admin/v1/me" },
  updateAdminProfile: { method: "PATCH", path: "/admin/v1/me" },
  changeOwnPassword: { method: "POST", path: "/admin/v1/me/password" },

  // Platform staff (this console's own accounts).
  listStaff: { method: "GET", path: "/admin/v1/staff" },
  createStaff: { method: "POST", path: "/admin/v1/staff" },
  getStaff: { method: "GET", path: "/admin/v1/staff/{id}" },
  updateStaff: { method: "PATCH", path: "/admin/v1/staff/{id}" },
  disableStaff: { method: "POST", path: "/admin/v1/staff/{id}/disable" },
  enableStaff: { method: "POST", path: "/admin/v1/staff/{id}/enable" },
  resetStaffPassword: { method: "POST", path: "/admin/v1/staff/{id}/reset-password" },
  revokeStaffSessions: { method: "DELETE", path: "/admin/v1/staff/{id}/sessions" },

  getSystemHealth: { method: "GET", path: "/admin/v1/health" },
  getOverviewMetrics: { method: "GET", path: "/admin/v1/overview" },
  listSystemMetrics: { method: "GET", path: "/admin/v1/system/metrics" },
  listSystemErrors: { method: "GET", path: "/admin/v1/system/errors" },
  updateSystemError: { method: "PATCH", path: "/admin/v1/system/errors/{id}" },
  listSystemEvents: { method: "GET", path: "/admin/v1/system/events" },
  streamAdminEvents: { method: "GET", path: "/admin/v1/events" },

  listOrganizations: { method: "GET", path: "/admin/v1/organizations" },
  createOrganization: { method: "POST", path: "/admin/v1/organizations" },
  createStore: { method: "POST", path: "/admin/v1/organizations/{id}/stores" },
  createDealerUser: { method: "POST", path: "/admin/v1/organizations/{id}/users" },
  removeOrganizationMember: { method: "DELETE", path: "/admin/v1/organizations/{id}/members/{userId}" },
  updateStore: { method: "PATCH", path: "/admin/v1/stores/{id}" },
  updateStorePolicy: { method: "PATCH", path: "/admin/v1/stores/{id}/policy" },
  deactivateStore: { method: "POST", path: "/admin/v1/stores/{id}/deactivate" },
  activateStore: { method: "POST", path: "/admin/v1/stores/{id}/activate" },
  setStoreMember: { method: "PUT", path: "/admin/v1/stores/{id}/members/{userId}" },
  resetUserPassword: { method: "POST", path: "/admin/v1/users/{id}/reset-password" },
  getOrganization: { method: "GET", path: "/admin/v1/organizations/{id}" },
  updateOrganization: { method: "PATCH", path: "/admin/v1/organizations/{id}" },
  suspendOrganization: { method: "POST", path: "/admin/v1/organizations/{id}/suspend" },
  reactivateOrganization: { method: "POST", path: "/admin/v1/organizations/{id}/reactivate" },
  listStores: { method: "GET", path: "/admin/v1/stores" },
  getStore: { method: "GET", path: "/admin/v1/stores/{id}" },
  listUsers: { method: "GET", path: "/admin/v1/users" },
  getUser: { method: "GET", path: "/admin/v1/users/{id}" },
  changeUserRole: { method: "PUT", path: "/admin/v1/users/{id}/role" },
  removeUserStoreMembership: { method: "DELETE", path: "/admin/v1/users/{id}/stores/{storeId}" },
  suspendUser: { method: "POST", path: "/admin/v1/users/{id}/suspend" },
  reactivateUser: { method: "POST", path: "/admin/v1/users/{id}/reactivate" },

  listOpportunities: { method: "GET", path: "/admin/v1/opportunities" },
  getOpportunity: { method: "GET", path: "/admin/v1/opportunities/{id}" },
  createActLink: { method: "POST", path: "/admin/v1/act" },
  getOpportunityTimeline: { method: "GET", path: "/admin/v1/opportunities/{id}/timeline" },
  listOpportunityImages: { method: "GET", path: "/admin/v1/opportunities/{id}/images" },
  listOpportunityNotes: { method: "GET", path: "/admin/v1/opportunities/{id}/notes" },
  clearSellerPrice: { method: "POST", path: "/admin/v1/opportunities/{id}/clear-seller-price" },
  // Development only (absent in production; AdminIdentity.devTools says whether they exist).
  getSmsSimulator: { method: "GET", path: "/admin/v1/dev/conversations/{id}/simulator" },
  simulateInboundSms: { method: "POST", path: "/admin/v1/dev/conversations/{id}/inbound" },
  listConversations: { method: "GET", path: "/admin/v1/conversations" },
  getConversation: { method: "GET", path: "/admin/v1/conversations/{id}" },
  listConversationMessages: { method: "GET", path: "/admin/v1/conversations/{id}/messages" },
  getMessageMedia: { method: "GET", path: "/admin/v1/messages/{id}/media/{index}" },
  getConversationTimeline: { method: "GET", path: "/admin/v1/conversations/{id}/timeline" },
  listAppointments: { method: "GET", path: "/admin/v1/appointments" },
  listPurchases: { method: "GET", path: "/admin/v1/purchases" },
  getPurchaseTrace: { method: "GET", path: "/admin/v1/purchases/{id}/trace" },

  getAiSummary: { method: "GET", path: "/admin/v1/ai/summary" },
  listAiRuns: { method: "GET", path: "/admin/v1/ai/runs" },
  getAiRun: { method: "GET", path: "/admin/v1/ai/runs/{id}" },
  getPromptSettings: { method: "GET", path: "/admin/v1/prompt-settings" },
  updatePromptSettings: { method: "PATCH", path: "/admin/v1/prompt-settings" },
  getOrganizationPromptSettings: { method: "GET", path: "/admin/v1/organizations/{id}/prompt-settings" },
  updateOrganizationPromptSettings: { method: "PATCH", path: "/admin/v1/organizations/{id}/prompt-settings" },
  getFollowupSettings: { method: "GET", path: "/admin/v1/followup-settings" },
  updateFollowupSettings: { method: "PUT", path: "/admin/v1/followup-settings" },
  resetFollowupSettings: { method: "DELETE", path: "/admin/v1/followup-settings" },
  getOrganizationFollowupSettings: { method: "GET", path: "/admin/v1/organizations/{id}/followup-settings" },
  updateOrganizationFollowupSettings: { method: "PUT", path: "/admin/v1/organizations/{id}/followup-settings" },
  resetOrganizationFollowupSettings: { method: "DELETE", path: "/admin/v1/organizations/{id}/followup-settings" },
  catchUpFollowups: { method: "POST", path: "/admin/v1/followups/catch-up" },
  listQueues: { method: "GET", path: "/admin/v1/queues" },
  getQueue: { method: "GET", path: "/admin/v1/queues/{name}" },
  listQueueJobs: { method: "GET", path: "/admin/v1/queues/{name}/jobs" },
  getQueueJob: { method: "GET", path: "/admin/v1/queues/{name}/jobs/{jobId}" },
  discardQueueJob: { method: "DELETE", path: "/admin/v1/queues/{name}/jobs/{jobId}" },
  retryQueueJob: { method: "POST", path: "/admin/v1/queues/{name}/jobs/{jobId}/retry" },
  pauseQueue: { method: "POST", path: "/admin/v1/queues/{name}/pause" },
  resumeQueue: { method: "POST", path: "/admin/v1/queues/{name}/resume" },

  listIntegrations: { method: "GET", path: "/admin/v1/integrations" },
  getIntegration: { method: "GET", path: "/admin/v1/integrations/{id}" },
  retryCrmEmail: { method: "POST", path: "/admin/v1/crm-emails/{id}/retry" },
  // Offer API integrations (connectors, write-only credentials, active switch, store links).
  listOfferDrivers: { method: "GET", path: "/admin/v1/offer-api/drivers" },
  listOfferIntegrations: { method: "GET", path: "/admin/v1/offer-integrations" },
  createOfferIntegration: { method: "POST", path: "/admin/v1/offer-integrations" },
  getOfferIntegration: { method: "GET", path: "/admin/v1/offer-integrations/{id}" },
  updateOfferIntegration: { method: "PATCH", path: "/admin/v1/offer-integrations/{id}" },
  listOfferRuns: { method: "GET", path: "/admin/v1/offer-integrations/{id}/runs" },
  getOfferRun: { method: "GET", path: "/admin/v1/offer-runs/{id}" },
  listOpportunityOfferRuns: { method: "GET", path: "/admin/v1/opportunities/{id}/offer-runs" },
  retryOfferRun: { method: "POST", path: "/admin/v1/offer-runs/{id}/retry" },
  linkStoreOfferIntegration: { method: "PUT", path: "/admin/v1/stores/{id}/offer-integrations/{integrationId}" },
  unlinkStoreOfferIntegration: { method: "DELETE", path: "/admin/v1/stores/{id}/offer-integrations/{integrationId}" },
  getAnalyticsReport: { method: "GET", path: "/admin/v1/analytics/{section}" },
  listAuditEvents: { method: "GET", path: "/admin/v1/audit" },
  getAuditEvent: { method: "GET", path: "/admin/v1/audit/{id}" },
  searchGlobal: { method: "GET", path: "/admin/v1/search" },
} as const satisfies Record<string, { method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; path: string }>;

export type OperationId = keyof typeof OPERATIONS;
